// Textos de «Tu agenda llena» para la vista previa del WhatsApp.
//
// HEAD / CARDS / OFFER son una COPIA por valor de admin_scripts/add_agenda_llena_templates.js (las plantillas
// ya aprobadas en Meta): la vista previa tiene que ser el cuerpo exacto que recibe el cliente. Si cambias una
// plantilla, cambia aquí también (agendaTextos.test.js fija los cuerpos). «va» se manda como «ca».
// Puro: sin React ni contexto.

const HEAD = { es: "📩 *{{1}}* te escribe:\n\n", ca: "📩 *{{1}}* t'escriu:\n\n" };

// Cada tarjeta: texto antes del cierre y cierre. Variables: {{1}} negocio, {{2}} nombre, luego las propias
// (volver: {{3}} tiempo {{4}} servicio · nuevo/hueco: {{3}}) y, con oferta, la siguiente libre.
const CARDS = {
  volver: {
    es: { pre: "Hola {{2}}, ya han pasado {{3}} desde tu último {{4}}.", end: "Cuando quieras te buscamos hueco.", vars: 4 },
    ca: { pre: "Hola {{2}}, ja han passat {{3}} des del teu últim {{4}}.", end: "Quan vulguis et busquem lloc.", vars: 4 },
  },
  nuevo: {
    es: { pre: "Hola {{2}}, gracias por venir hace {{3}}.", end: "Si quieres repetir, te buscamos hueco cuando te venga bien.", vars: 3 },
    ca: { pre: "Hola {{2}}, gràcies per venir fa {{3}}.", end: "Si vols repetir, et busquem lloc quan et vagi bé.", vars: 3 },
  },
  perdido: {
    es: { pre: "Hola {{2}}, hace tiempo que no te vemos por aquí.", end: "Si te apetece volver, esta semana tenemos hueco.", vars: 2 },
    ca: { pre: "Hola {{2}}, fa temps que no et veiem per aquí.", end: "Si et ve de gust tornar, aquesta setmana tenim lloc.", vars: 2 },
  },
  hueco: {
    es: { pre: "Hola {{2}}, tenemos hueco {{3}}.", end: "Si te viene bien, resérvalo desde aquí.", vars: 3 },
    ca: { pre: "Hola {{2}}, tenim lloc {{3}}.", end: "Si et va bé, reserva'l des d'aquí.", vars: 3 },
  },
};

/** Idioma de la plantilla: va (valenciano) y ca comparten texto; cualquier otro cae a castellano. */
export const plantillaLang = (lang) => (lang === "va" || lang === "ca" ? "ca" : "es");
/** «retrasado» no tiene plantilla propia: usa la de «perdido». */
const cardKey = (tarjeta) => (tarjeta === "retrasado" ? "perdido" : tarjeta);

/** Cuerpo de la plantilla con sus {{n}} sin sustituir (el mismo que se registró en Meta). */
export function plantillaBody(tarjeta, lang, oferta) {
  const l = plantillaLang(lang);
  const c = (CARDS[cardKey(tarjeta)] || CARDS.perdido)[l];
  return oferta
    ? `${HEAD[l]}${c.pre}\n\n🎁 {{${c.vars + 1}}}\n\n${c.end}`
    : `${HEAD[l]}${c.pre} ${c.end}`;
}

const unaLinea = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim();
/** Longitud máxima de la oferta (igual que el backend: OFERTA_MAX de agendaSend.js / c_clientInsightsApi). */
export const OFERTA_MAX = 60;
/** Tope de gasto mensual del automático por defecto (euros). */
export const TOPE_DEFECTO_EUR = 10;
/** Envíos máximos por empresa y día (MAX_DIA del backend). */
export const MAX_DIA = 100;

/**
 * Texto que verá el cliente. `tiempo` y `servicio` solo cuentan en «volver»; `tiempo` también en «nuevo»;
 * `hueco` (ya redactado, ver huecoTexto) en «hueco». Idéntico al cuerpo de la plantilla con los parámetros puestos.
 */
export function previewTexto({ tarjeta, negocio, nombre, tiempo, servicio, hueco, oferta, lang = "es" }) {
  const key = cardKey(tarjeta);
  const of = unaLinea(oferta).slice(0, OFERTA_MAX).trim();
  const params = [unaLinea(negocio), unaLinea(nombre) || "cliente"];
  if (key === "volver") params.push(unaLinea(tiempo), unaLinea(servicio || "cita").toLowerCase());
  else if (key === "nuevo") params.push(unaLinea(tiempo));
  else if (key === "hueco") params.push(unaLinea(hueco));
  if (of) params.push(of);
  return plantillaBody(key, lang, !!of).replace(/\{\{(\d+)\}\}/g, (_, n) => params[Number(n) - 1] ?? "");
}

const DAY_MS = 24 * 3600 * 1000;
const diasEntre = (desde, hasta) => {
  const [y1, m1, d1] = String(desde).split("-").map(Number);
  const [y2, m2, d2] = String(hasta).split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / DAY_MS);
};

/** «5 semanas» / «2 meses»: misma regla que tiempoDesde de agendaSend.js (semanas hasta 44 días, luego meses con floor). */
export function tiempoDesde(ultima, hoy, lang = "es") {
  const ca = plantillaLang(lang) === "ca";
  const d = diasEntre(ultima, hoy);
  if (d < 45) {
    const s = Math.round(d / 7);
    return s <= 1 ? (ca ? "una setmana" : "una semana") : `${s} ${ca ? "setmanes" : "semanas"}`;
  }
  const m = Math.floor(d / 30); // R15: floor, como agendaSend.js (45 días = «un mes»)
  return m <= 1 ? "un mes" : `${m} ${ca ? "mesos" : "meses"}`;
}

/** «el martes por la tarde» / «dimarts a la tarda» para el parámetro {{3}} de la plantilla de hueco. */
export function huecoTexto({ day, franja, lang = "es" }) {
  const ca = plantillaLang(lang) === "ca";
  const [y, m, d] = String(day).split("-").map(Number);
  const dia = new Intl.DateTimeFormat(ca ? "ca-ES" : "es-ES", { weekday: "long", timeZone: "UTC" })
    .format(new Date(Date.UTC(y, m - 1, d, 12)));
  if (ca) return `${dia} ${franja === "manana" ? "al matí" : "a la tarda"}`;
  return `el ${dia} ${franja === "manana" ? "por la mañana" : "por la tarde"}`;
}

/** Locale de Intl para el idioma de la app (va = valenciano -> ca). */
export const intlLocale = (lang) => (lang === "va" || lang === "ca" ? "ca-ES" : lang === "en" ? "en-GB" : "es-ES");

/** «7,20 €» (o «7.2 €» según idioma). Con `entero` sin decimales, para las estimaciones. */
export function formatEuros(n, lang = "es", { entero = false } = {}) {
  return new Intl.NumberFormat(intlLocale(lang), {
    style: "currency", currency: "EUR",
    minimumFractionDigits: entero ? 0 : 2, maximumFractionDigits: entero ? 0 : 2,
  }).format(Number(n) || 0);
}

/** Fecha de hoy en Madrid (YYYY-MM-DD), como la que usa el backend para «hace cuánto». */
export const hoyMadrid = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(new Date());
